import { terraContract } from '@terra/shared-authorization';
import {
  TerraEmbeddingRoutingSchema,
  TerraInvocationRoutingSchema,
  TenantModelRouterBulkMutationSchema,
  TenantModelRouterCatalogObjectSchema,
  TenantModelRouterDeleteResponseSchema,
  TenantModelRouterInvocationParamsSchema,
  TenantModelRouterInvocationRuleParamsSchema,
  TenantModelRouterMutationResponseSchema,
  TenantModelRouterResponseSchema,
  TenantModelRouterRuleIdParamsSchema,
} from '@terra/shared-contracts-tenant-model-router';
import { z } from 'zod';
import {
  COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS,
  COCKPIT_VIEWER_PERMISSION,
} from './cockpit-permissions';

export const cockpitTenantModelRouterRoutes = {
  getTenantModelRouterCatalog: terraContract
    .meta({ permissions: [COCKPIT_VIEWER_PERMISSION] })
    .route({
      method: 'GET',
      path: '/tenant-model-router/catalog',
      summary: 'Get tenant model router catalog',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: z.object({}) }))
    .output(TenantModelRouterCatalogObjectSchema),
  getTenantModelRouter: terraContract
    .meta({ permissions: [COCKPIT_VIEWER_PERMISSION] })
    .route({
      method: 'GET',
      path: '/tenant-model-router',
      summary: 'Get Terra invocation and embedding routing',
      inputStructure: 'detailed',
    })
    .output(TenantModelRouterResponseSchema),
  upsertTerraInvocationRouting: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/tenant-model-router/invocations/{invocation}',
      summary: 'Replace Terra invocation routing with one rule',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: TerraInvocationRoutingSchema,
        params: TenantModelRouterInvocationParamsSchema,
      }),
    )
    .output(TenantModelRouterMutationResponseSchema),
  createTerraInvocationRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'POST',
      path: '/tenant-model-router/invocations/{invocation}/rules',
      summary: 'Add a Terra invocation routing rule',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: TerraInvocationRoutingSchema,
        params: TenantModelRouterInvocationParamsSchema,
      }),
    )
    .output(TenantModelRouterMutationResponseSchema),
  updateTerraInvocationRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/tenant-model-router/invocations/{invocation}/rules/{id}',
      summary: 'Update a Terra invocation routing rule',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: TerraInvocationRoutingSchema,
        params: TenantModelRouterInvocationRuleParamsSchema,
      }),
    )
    .output(TenantModelRouterMutationResponseSchema),
  deleteTerraInvocationRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'DELETE',
      path: '/tenant-model-router/invocations/{invocation}/rules/{id}',
      summary: 'Delete a Terra invocation routing rule',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantModelRouterInvocationRuleParamsSchema }))
    .output(TenantModelRouterMutationResponseSchema),
  bulkMutateTerraInvocationRouting: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'PATCH',
      path: '/tenant-model-router/invocations',
      summary: 'Apply or reset multiple Terra invocation routing rules',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TenantModelRouterBulkMutationSchema }))
    .output(TenantModelRouterMutationResponseSchema),
  deleteTerraInvocationRouting: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'DELETE',
      path: '/tenant-model-router/invocations/{invocation}',
      summary: 'Delete all Terra invocation routing for an invocation',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantModelRouterInvocationParamsSchema }))
    .output(TenantModelRouterDeleteResponseSchema),
  createTerraEmbeddingRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'POST',
      path: '/tenant-model-router/embeddings/rules',
      summary: 'Add a Terra embedding routing rule',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: TerraEmbeddingRoutingSchema }))
    .output(TenantModelRouterMutationResponseSchema),
  updateTerraEmbeddingRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'PUT',
      path: '/tenant-model-router/embeddings/rules/{id}',
      summary: 'Update a Terra embedding routing rule',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        body: TerraEmbeddingRoutingSchema,
        params: TenantModelRouterRuleIdParamsSchema,
      }),
    )
    .output(TenantModelRouterMutationResponseSchema),
  deleteTerraEmbeddingRoutingRule: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'DELETE',
      path: '/tenant-model-router/embeddings/rules/{id}',
      summary: 'Delete a Terra embedding routing rule',
      inputStructure: 'detailed',
    })
    .input(z.object({ params: TenantModelRouterRuleIdParamsSchema }))
    .output(TenantModelRouterMutationResponseSchema),
  deleteTerraEmbeddingRouting: terraContract
    .meta({ permissions: [...COCKPIT_FEATURE_FLAG_WRITE_PERMISSIONS] })
    .route({
      method: 'DELETE',
      path: '/tenant-model-router/embeddings',
      summary: 'Delete all Terra embedding routing',
      inputStructure: 'detailed',
    })
    .output(TenantModelRouterDeleteResponseSchema),
} as const;
